import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';

import {
  DocumentSummaryBundleConfig,
  getDocumentSummaryBundleLabel,
} from './documentSummaryBundleConfig/DocumentSummaryBundleConfigModal.utils';

/**
 * Minimal bundle shape accepted by getTemplateBundleLabel. Both the editor
 * bundle type and the selection modal's GraphQL fragment satisfy this.
 */
interface BundleLabelInput {
  kind: PresentationBundleKind;
  /** The first page's kind, if available. */
  firstPageKind?: PresentationPageKind;
  /** The first page's configured name, if applicable. */
  firstPageName?: string | null;
  /**
   * The user-entered title on the first page (a cover page's display title or
   * a custom page's name). Shown as subtext beneath the bundle label.
   */
  firstPageTitle?: string | null;
  /** Divider config, if applicable. Holds the divider's user-entered title. */
  dividerTemplateBundleConfiguration?: {
    dividerTitle?: string | null;
  } | null;
  /** Document summary config, if applicable. */
  documentSummaryTemplateBundleConfiguration?:
    | DocumentSummaryBundleConfig
    | null
    | undefined;
}

interface BundleLabelPageInput {
  kind: PresentationPageKind;
  customPage?: {
    pageName?: string | null;
  } | null;
  diagramPage?: {
    pageName?: string | null;
  } | null;
  coverPage?: {
    displayTitle?: string | null;
  } | null;
}

/**
 * Returns the display label for a template bundle. Handles custom page
 * names, document summary config labels, and falls back to the generic
 * bundle/page display name.
 */
export function getTemplateBundleLabel(bundle: BundleLabelInput): string {
  if (bundle.kind === PresentationBundleKind.CustomPageBundle) {
    if (bundle.firstPageName) {
      return bundle.firstPageName;
    }
  }

  if (bundle.kind === PresentationBundleKind.DocumentSummaryBundle) {
    const configLabel = getDocumentSummaryBundleLabel(
      bundle.documentSummaryTemplateBundleConfiguration
    );
    if (configLabel) return configLabel;
  }

  return getTemplateBundleDisplayName(bundle.kind, bundle.firstPageKind);
}

export function getTemplateBundleLabelPageInput(
  firstPage?: BundleLabelPageInput | null
): Pick<
  BundleLabelInput,
  'firstPageKind' | 'firstPageName' | 'firstPageTitle'
> {
  return {
    firstPageKind: firstPage?.kind,
    firstPageName:
      firstPage?.customPage?.pageName || firstPage?.diagramPage?.pageName,
    // Diagram pages are deliberately excluded: their name defaults to the
    // page kind's own display name, so surfacing it as subtext would just
    // repeat the label. They keep using their name as the label instead.
    firstPageTitle:
      firstPage?.coverPage?.displayTitle || firstPage?.customPage?.pageName,
  };
}

/**
 * Returns the user-entered title for bundles that carry one — cover, divider,
 * and custom pages — to be shown as subtext beneath the bundle label. Returns
 * null for every other bundle kind, and for title-bearing bundles whose title
 * is still blank.
 */
export function getTemplateBundleSubLabel(
  bundle: BundleLabelInput
): string | null {
  if (bundle.kind === PresentationBundleKind.DividerBundle) {
    // Divider titles live on the bundle configuration, not on a page.
    return bundle.dividerTemplateBundleConfiguration?.dividerTitle || null;
  }

  if (
    bundle.kind === PresentationBundleKind.CoverSlideBundle ||
    bundle.kind === PresentationBundleKind.CustomPageBundle
  ) {
    return bundle.firstPageTitle || null;
  }

  return null;
}

export function getTemplateBundleDisplayName(
  bundleKind: PresentationBundleKind,
  pageKind?: PresentationPageKind
): string {
  switch (pageKind) {
    case PresentationPageKind.CoverPage:
      return 'Cover page';
    case PresentationPageKind.CustomPage:
      return 'Custom page';
    case PresentationPageKind.EstatePlanningGlossary:
      return 'Estate & planning glossary: Key terms & documents';
    case PresentationPageKind.WhyEstatePlanningMatters:
      return 'Why estate planning matters';
    case PresentationPageKind.NoEstatePlanningConsequences:
      return 'Potential consequences of not doing proper planning';
    case PresentationPageKind.SettingUpEstatePlan:
      return 'Setting up an estate plan';
    case PresentationPageKind.EstatePlanningChecklist:
      return 'Estate planning checklist for major life events';
    case PresentationPageKind.ManagingRecentInheritance:
      return 'Smart strategies for managing your inheritance';
    case PresentationPageKind.EstateTaxesExplainer:
      return 'Gift and estate taxes';
    case PresentationPageKind.StateInheritanceTaxExplainer:
      return 'State estate and inheritance taxes';
    case PresentationPageKind.GstExplainer:
      return 'Generation-skipping transfer (GST) tax';
    case PresentationPageKind.WhyUseTrust:
      return 'Why use a trust?';
    case PresentationPageKind.TrustBasics:
      return 'Trust basics';
    case PresentationPageKind.PopularPlanningStrategies:
      return 'Popular estate planning strategies to consider';
    case PresentationPageKind.CltStrategyExplainer:
      return 'Charitable Lead Trust (CLT)';
    case PresentationPageKind.CrtStrategyExplainer:
      return 'Charitable Remainder Trust (CRT)';
    case PresentationPageKind.GratStrategyExplainer:
      return 'Grantor Retained Annuity Trust (GRAT)';
    case PresentationPageKind.InstallmentSaleStrategyExplainer:
      return 'Installment Sale & Loan';
    case PresentationPageKind.IlitStrategyExplainer:
      return 'Irrevocable Life Insurance Trust (ILIT)';
    case PresentationPageKind.SlatStrategyExplainer:
      return 'Spousal Lifetime Access Trust (SLAT)';
    case PresentationPageKind.QprtStrategyExplainer:
      return 'Qualified Personal Residence Trust (QPRT)';
    case PresentationPageKind.FlpStrategyExplainer:
      return 'Family Limited Partnership (FLP)';
    case PresentationPageKind.PhilanthropicPlanningExplainer:
      return 'Philanthropic Planning: Donor-Advised Funds and Private Foundations';
    case PresentationPageKind.Divider:
      return 'Divider page';

    // intentionally not handling the default case here; that'll happen below in the
    // bundleKind check
  }

  switch (bundleKind) {
    case PresentationBundleKind.CoverSlideBundle:
      return 'Cover page';
    case PresentationBundleKind.TableOfContentsBundle:
      return 'Table of contents';
    case PresentationBundleKind.BalanceSheetBundle:
      return 'Balance sheet';
    case PresentationBundleKind.FamilyTreeBundle:
      return 'Family tree';
    case PresentationBundleKind.EstateInsightsBundle:
      return 'Insights';
    case PresentationBundleKind.EntitiesOverviewBundle:
      return 'Entities list';
    // Matches the runtime designer's label for this bundle kind
    // (getBundleDisplayNames in ClientPresentationDesignerTreeView.constants).
    case PresentationBundleKind.EntitySummaryBundle:
      return 'Entity overview';
    case PresentationBundleKind.DocumentSummaryBundle:
      return 'Document summaries';
    case PresentationBundleKind.LifeInsuranceBundle:
      return 'Life insurance';
    case PresentationBundleKind.LiabilitiesBundle:
      return 'Liabilities';
    case PresentationBundleKind.ProfessionalTeamBundle:
      return 'Professional team';
    case PresentationBundleKind.LuminaryDisclaimerBundle:
      return 'Luminary disclaimer';
    case PresentationBundleKind.StandaloneDisclaimerBundle:
      return 'Firm disclaimer';
    case PresentationBundleKind.CustomPageBundle:
      return 'Custom page';
    case PresentationBundleKind.DividerBundle:
      return 'Divider page';
    case PresentationBundleKind.WaterfallOverviewBundle:
      return 'Waterfall overview';
    case PresentationBundleKind.BeneficiaryReportBundle:
      return 'Beneficiary report';
    case PresentationBundleKind.WaterfallComparisonBundle:
      return 'Waterfall comparison';
    case PresentationBundleKind.DocumentsReviewedBundle:
      return 'Documents reviewed';
    case PresentationBundleKind.FamilyOverviewBundle:
      return 'Family overview';
    case PresentationBundleKind.WillBundle:
      return 'Wills';
    case PresentationBundleKind.PowerOfAttorneyBundle:
      return 'Powers of Attorney';
    case PresentationBundleKind.HealthcareDirectiveBundle:
      return 'Healthcare directives';
    case PresentationBundleKind.TrustOverviewBundle:
      return 'Trusts overview';
    case PresentationBundleKind.BusinessOverviewBundle:
      return 'Businesses overview';
    case PresentationBundleKind.DiagramBundle:
      return 'Asset distribution diagram';
    case PresentationBundleKind.AssetDistributionBundle:
      return 'Asset distribution detail';
    case PresentationBundleKind.PlanningOpportunitiesBundle:
      return 'Observations & opportunities';
    default:
      return EMPTY_CONTENT_HYPHEN;
  }
}
